// @flow
import _ from 'lodash'
import React, { Fragment, Component } from 'react'
import AdsListContainer from './AudienceEdit/AdsListContainer'
import ProvidersSetContainer from './AudienceEdit/ProvidersSetContainer'
import PlacementTypeSelectContainer from './AudienceEdit/PlacementTypeSelectContainer'
import PaymentTypeSelect from './AudienceEdit/PaymentTypeSelect'
import PriceRecommendations from './AudienceEdit/PriceRecommendations'
import PriceInputContainer from './AudienceEdit/PriceInputContainer'
import IncludedLocationsSelectContainer from './AudienceEdit/IncludedLocationsSelectContainer'
import ExcludedLocationsSelectContainer from './AudienceEdit/ExcludedLocationsSelectContainer'
import GenderSelectContainer from './AudienceEdit/GenderSelectContainer'
import AgeRangeContainer from './AudienceEdit/AgeRangeContainer'
import PlatformSelect from './AudienceEdit/PlatformSelect'
import { translations, translateFunc } from '../shared/localize'
import Interests from './AudienceEdit/Interests'
import Demographics from './AudienceEdit/Demographics'
import SocialPublics from './AudienceEdit/SocialPublics'
import PlacementsContainer from './AudienceEdit/PlacementsContainer'
import ScriptDependencyRender from './controls/ScriptDependencyRender'
import AdditionalParameters from './controls/AdditionalParameters'
import Checkbox from './controls/Checkbox'
import Geography from './AudienceEdit/Geography'
import ErrorText from './controls/ErrorText'
import FloatPanel from './controls/FloatPanel'
import { PanelHint } from './AdEditWrapper'
import styled, { type StyledComponent } from 'styled-components'
import FloatingWidget from './AudienceEdit/FloatingWidget'
import VerticallyCentralizedItems from './controls/VerticallyCentralizedItems'
import SegmentationContainer from './AudienceEdit/SegmentationContainer'
import { Audience, audienceInputsWidth } from './constants'
import ExcludedVkAppsTextArea from './AudienceEdit/ExcludedVkAppsTextArea'
import KeywordsTextAreaContainer from './AudienceEdit/KeywordsTextAreaContainer'
import AboutCompanyContainer from './AudienceEdit/AboutCompanyContainer'

import type { Node as ReactNode } from 'react'
import type {
  AudienceData,
  Configs,
  OptionType,
  Conversion as ConversionType,
  VkGroup,
  GroupedStringParameters,
  ProviderWorkPosition,
  Public
} from './types'
import type { Initial, State as AudienceState, Errors } from '../redux/ducks/audienceEdit/reducers'

import { initializeStyler } from '../main/legacy/initialize.forms'

// Common Parameters
import BudgetSelectContainer from './AudienceEdit/BudgetSelectContainer'
import TotalLimitContainer from './AudienceEdit/TotalLimitContainer'
import Switcher from './controls/Switcher'
import ShowtimeWidget from './ShowtimeWidget'
import AdAccountSelectContainer from './AudienceEdit/AdAccountSelectContainer'
import CampaignSelectContainer from './AudienceEdit/CampaignSelectContainer'
import FinishDateContainer from './AudienceEdit/FinishDateContainer'
import AudienceNameContainer from './AudienceEdit/AudienceNameContainer'

// Targeting
import VkGroupsSelect from './VkGroupsSelect'

// Retargeting | Targeting
import TargetingSourcesContainer from './TargetingSourcesContainer'

// Micro Locations
import MicrolocationsMap, { Help as MicrolocationsMapHelp } from './MicrolocationsMap'
import LocalGeoType from './MicrolocationsMap/LocalGeoType'

// ProductRetargeting
import PricelistSelectContainer from './AudienceEdit/PricelistSelectContainer'
import HintWrapper from './controls/HintWrapper'
import LabeledWrapper from './AudienceEdit/LabeledWrapper'
import PrimaryButton from './controls/PrimaryButton'
import Select from './controls/Select'
import NativeSelect from './controls/NativeSelect'
import MultiSelect from './controls/MultiSelect'

// Conversions
import Conversion from './AudienceEdit/Conversion'
import WhatIsItHint from './controls/WhatIsItHint'
import PrefixNameContainer from './AudienceEdit/PrefixNameContainer'
import SegmentationPreviewContainer from './AudienceEdit/SegmentationPreviewContainer'
import LeadformSelectContainer from './AudienceEdit/LeadformSelectContainer'
import VkDisclaimersContainer from './AudienceEdit/VkDisclaimersContainer'
import IncludedVkGroupsSelectContainer from './AudienceEdit/IncludedVkGroupsSelectContainer'
import IncludedVkAppsTextAreaContainer from './AudienceEdit/IncludedVkAppsTextAreaContainer'
import VkActivityGroupsSelectContainer from './AudienceEdit/VkActivityGroupsSelectContainer'
import AudienceNameTemplateSelectContainer from './AudienceEdit/AudienceNameTemplateSelectContainer'
import BiddingModeSelect from './AudienceEdit/BiddingModeSelect'
import TargetMailShowsLimitInfoContainer from './AudienceEdit/TargetMailShowsLimitInfoContainer'
import VkShowsLimitInfo from './AudienceEdit/VkShowsLimitInfo'
import AudienceStatusSelect from './AudienceEdit/AudienceStatusSelect'
import VkEventGroupsContainer from './AudienceEdit/VkEventGroups/VkEventGroupsContainer'
import ProviderPositionsSelect from './AudienceEdit/ProviderPositionsSelect'

export type Visibility = {|
  audienceType: boolean,
  placementType: boolean,
  segmentation: boolean,
  providers: boolean,
  fbPlacements: boolean,
  vkPlacement: boolean,
  vkPublic: boolean,
  fbPublic: boolean,
  advertisements: boolean,
  budgetPortions: boolean,
  campaign: boolean,
  conversionId: boolean,
  vk: boolean,
  tm: boolean,
  fb: boolean,
  gl: boolean,
  adAccountSelect: boolean,
  leadformId: boolean,
  vkEventGroups: boolean,
  vkBiddingModeSelect: boolean,
  tmBiddingModeSelect: boolean,
  fbBiddingModeSelect: boolean,
  vkShowsLimitInfo: boolean,
  tmShowsLimitInfo: boolean
|}

type Props = {|
  ...AudienceState,
  vkPlacementOptions: Array<OptionType>,
  initial: Initial,
  visibility: Visibility,
  hasFormErrors: boolean,
  isNewAction: boolean,
  isCopyAction: boolean,
  isEditAction: boolean,
  isProductRetargeting: boolean,
  isConversions: boolean,
  isSegmenting: boolean,
  isLeadform: boolean,
  showPriceRecommendationWidget: boolean,
  hasAdvertisementsErrors: boolean,
  showGeography: boolean,
  action: 'create' | 'update' | 'copy',
  onInit: () => void,
  configs: Configs,
  onPersist: () => void,
  onLoadEstimates: () => void,
  onTriggerEstimates: () => void,
  onVkBiddingModeChange: (string) => void,
  onTmBiddingModeChange: (string) => void,
  onFbBiddingModeChange: (string) => void,
  onVkShowsLimitChange: (string) => void,
  onStatusChange: (string) => void,
  onIsAdminFieldShownChange: (boolean) => void,
  onBudgetDevelopmentChange: (string) => void,
  onShowtimeChange: (Array<Array<number>>) => void,
  onAgeLimitChange: (string) => void,
  onImproveAdvertisementsChange: (boolean) => void,
  onBudgetPortionsChange: (string) => void,
  onConversionIdChange: (number) => void,
  onConversionsChange: (Array<ConversionType>) => void,
  onBirthdayChange: (string) => void,
  onRelationshipStatusChange: (string) => void,
  onLoadedIncludedGroupsChange: (Array<VkGroup>) => void,
  onLoadedExcludedGroupsChange: (Array<VkGroup>) => void,
  onIncludedVkAppsGroupsChange: (Array<GroupedStringParameters>) => void,
  onExcludedVkAppsChange: (string) => void,
  onLoadedVkActivityGroupsChange: (Array<VkGroup>) => void,
  onLocalGeoDataChange: ({|
    vkLocalGeoType: string,
    tmLocalGeoVisitType: string,
    tmLocalGeoLocType: string,
    fbLocalGeoType: string
  |}) => void,
  onPricelistIdChange: (number) => void,
  onOnlyTravellersChange: (boolean) => void,
  onVkUserDevicesChange: (Array<number>) => void,
  onVkPositionsChange: (Array<ProviderWorkPosition>) => void,
  onKeywordsGroupsChange: (Array<GroupedStringParameters>) => void,
  onVkPublicChange: (Public) => void,
  onFbPublicChange: (Public) => void,
  onIncludedGroupsChange: (Array<Object>) => void,
  onExcludedGroupsChange: (Array<Object>) => void,
  onVkActivityGroupsChange: (Array<Object>) => void,
  onPaymentTypeChange: (string) => void,
  onInterestIdsChange: (Array<number>) => void,
  onFbAdInterestsChange: (Array<Object>) => void,
  onDemographicIdsChange: (Array<number>) => void,
  onLocalGeoChange: (Array<Object>) => void,
  onGeographyTypeChange: (string) => void,
  onPlatformChange: (string) => void,
  onVkPlacementChange: (string) => void,
  onSetCopyDefaultValues: () => void,
  onFbPositionsChange: (Array<ProviderWorkPosition>) => void
|}

export type { Errors }

const t = translateFunc(translations.audience_edit)

export const InputCell: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 480px;
`
export const SmallInputCell: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 200px;
`
const RetargetingHint: StyledComponent<Object, any, any> = styled.div`
  width: ${audienceInputsWidth}px;
`
const RetargetingHintElementWrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 10px -10px;
`
const RetargetingHintTitle: StyledComponent<Object, any, any> = styled.h2`
  padding-left: 10px;
`
const RetargetingHintQuestionMark: StyledComponent<Object, any, any> = styled.div`
  padding-left: 10px;
`
const TextError: StyledComponent<Object, any, any> = styled.div`
  color: #e32d13;
`
const ImproveAds: StyledComponent<Object, any, any> = styled.div`
  .jq-checkbox {
    display: inline-block;
    vertical-align: middle;
    position: relative;
  }
`
const ImproveAdsHint: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 460px;
  margin-left: 20px;
  vertical-align: middle;
  padding: 5px 3px 2px 0;
  color: grey;
  font-size: 12px;
`
const VkUserDevicesSelect: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 480px;
`

export default class AudienceEdit extends Component<Props> {
  constructor(props: Props) {
    super(props)

    props.onInit()

    window.audienceEditComponent = this // for tests
  }

  render() {
    if (!this.props.initialized) return null
    return (
      <Fragment>
        {this.renderCommonAttributes(() => (
          <Fragment>
            {this.renderConversions()}
            {this.renderLeadform()}
            {this.renderProductRetargeting()}
          </Fragment>
        ))}

        {!this.props.isProductRetargeting && <h2>{t('select_parameters')}</h2>}

        <FloatingWidget widget={this.priceRecommendationsWidget()}>
          {this.renderAudienceTypeWidgets()}
          {this.renderAudienceTypeAttributes()}
          {this.renderCommonParameters()}
        </FloatingWidget>

        {this.renderAdvertisementWidget()}
        {this.renderFooter()}
      </Fragment>
    )
  }

  renderAudienceTypeAttributes() {
    switch (this.props.audienceType) {
      case Audience.types.targeting:
      case Audience.types.conversions:
      case Audience.types.posts_promotion:
      case Audience.types.leadform:
        return this.renderTargeting()
      case Audience.types.retargeting:
        return this.renderRetargeting()
      case Audience.types.product_retargeting:
        return null
      default:
        return `Unknown audience type ${this.props.audienceType}`
    }
  }

  renderFooter() {
    const t = translateFunc(translations.audience_edit.footer)

    return (
      <FloatPanel>
        <div className="container">
          <VerticallyCentralizedItems>
            <PrimaryButton
              height={32}
              disabled={this.props.hasAdvertisementsErrors || this.props.isLoading}
              onClick={() => this.props.onPersist()}
            >
              {t(this.props.action)}
              {this.props.isLoading && ' ...'}
            </PrimaryButton>
            <PanelHint>
              {' '}
              {this.props.isCopyAction ? (
                this.props.hasAdvertisementsErrors && (
                  <TextError>{t('advertisements_has_error')}</TextError>
                )
              ) : (
                <div>{t('and_go_to_create_advertisements')}</div>
              )}
            </PanelHint>
          </VerticallyCentralizedItems>
        </div>
      </FloatPanel>
    )
  }

  renderAdvertisementWidget() {
    const t = translateFunc(translations.audience_edit.advertisement_widget)

    return (
      this.props.visibility.advertisements && (
        <Fragment>
          <h2>{t('select_advertisements')}</h2>
          {this.renderAdsList()}
        </Fragment>
      )
    )
  }

  renderCommonParameters() {
    const t = translateFunc(translations.audience_edit.common_attributes)
    return (
      <Fragment>
        <h2>{t('budget_label')}</h2>

        <div>
          {this.renderBudget()}
          {this.renderTotalLimit()}
          {this.renderBudgetDevelopment()}
          {this.renderPaymentType()}
          {this.props.visibility.vkBiddingModeSelect && (
            <BiddingModeSelect
              className="t-VkBiddingModeSelect"
              provider="vk"
              paymentType={this.props.paymentType}
              value={this.props.vkBiddingMode}
              onChange={this.props.onVkBiddingModeChange}
            />
          )}
          {this.props.visibility.tmBiddingModeSelect && (
            <BiddingModeSelect
              className="t-TmBiddingModeSelect"
              provider="target_mail"
              paymentType={this.props.paymentType}
              value={this.props.tmBiddingMode}
              onChange={this.props.onTmBiddingModeChange}
            />
          )}
          {this.props.visibility.fbBiddingModeSelect && (
            <BiddingModeSelect
              className="t-FbBiddingModeSelect"
              provider="facebook"
              paymentType={this.props.paymentType}
              value={this.props.fbBiddingMode}
              onChange={this.props.onFbBiddingModeChange}
            />
          )}
          {this.props.visibility.vkShowsLimitInfo && (
            <VkShowsLimitInfo
              value={this.props.vkShowsLimit}
              onChange={this.props.onVkShowsLimitChange}
            />
          )}
          {this.props.visibility.tmShowsLimitInfo && <TargetMailShowsLimitInfoContainer />}
          <PriceInputContainer showLabel />
          <FinishDateContainer />
        </div>
        {this.renderShowtime()}

        {this.renderLegalInformation()}
        {this.props.isNewAction && (
          <div>
            <h2>{t('audience_status_label')}</h2>
            <AudienceStatusSelect value={this.props.status} onChange={this.props.onStatusChange} />
          </div>
        )}
        {/* Admin hack header */}
        <h2
          onClick={() => {
            this.props.initial.isAdmin &&
              this.props.onIsAdminFieldShownChange(!this.props.isAdminFieldShown)
          }}
        >
          {t('campaign_label')}
          {!this.props.initial.isPersisted && ' ' + t('campaign_creation_label')}
        </h2>
        <div>
          <AudienceNameTemplateSelectContainer />
          {!this.props.isSegmenting && <AudienceNameContainer />}
          <AdAccountSelectContainer visible={this.props.visibility.adAccountSelect} />
          <CampaignSelectContainer visible={this.props.visibility.campaign} />
        </div>

        {this.renderImproveAdvertisements()}
        {this.renderAdminBudgetPortions()}
        {this.props.isSegmenting && (
          <Fragment>
            <PrefixNameContainer />
            <SegmentationPreviewContainer />
          </Fragment>
        )}
      </Fragment>
    )
  }

  renderBudget() {
    const { budget } = this.props.initial

    return (
      <LabeledWrapper
        className="audience_budget"
        errors={this.props.budgetErrors}
        label={budget.label}
        twoLines
      >
        <BudgetSelectContainer />
      </LabeledWrapper>
    )
  }

  renderTotalLimit() {
    const { totalLimit } = this.props.initial

    return (
      <LabeledWrapper
        className="audience_total_limit"
        errors={this.props.totalLimitErrors}
        label={totalLimit.label}
        twoLines
      >
        <TotalLimitContainer />
      </LabeledWrapper>
    )
  }

  renderBudgetDevelopment() {
    const { budgetDevelopment } = this.props.initial

    return (
      <LabeledWrapper className="audience_budget" label={budgetDevelopment.label} twoLines>
        <Switcher
          options={_.map(budgetDevelopment.options, ([name, id]) => ({ id, name }))}
          value={this.props.budgetDevelopment}
          onChange={this.props.onBudgetDevelopmentChange}
        />
      </LabeledWrapper>
    )
  }

  renderShowtime() {
    const { showtime } = this.props.initial

    return (
      <LabeledWrapper label={showtime.label} errors={this.props.showtimeErrors} twoLines>
        <ShowtimeWidget value={this.props.showtime} onChange={this.props.onShowtimeChange} />
      </LabeledWrapper>
    )
  }

  renderAgeLimit() {
    if (!this.props.visibility.vk && !this.props.visibility.tm) return null

    const { ageLimit } = this.props.initial

    return (
      <LabeledWrapper label={ageLimit.label} errors={this.props.ageLimitErrors} twoLines>
        <InputCell className="age-limit-wrapper">
          <Select
            value={this.props.ageLimit || ''}
            options={[
              { id: '', name: ageLimit.disabled },
              ...ageLimit.options.map((item) => ({
                id: item[1],
                name: item[0]
              }))
            ]}
            noResultsText={t('age_limit.no_results')}
            onChange={this.props.onAgeLimitChange}
          />
        </InputCell>
      </LabeledWrapper>
    )
  }

  renderImproveAdvertisements() {
    const { label } = this.props.initial.improveAdvertisements
    const { improveAdvertisements, improveAdvertisementsErrors } = this.props
    const t = translateFunc(translations.audience_edit.improve_advertisements)

    const labelNode = (
      <span
        className="t-ImproveAds__title"
        onClick={() => this.props.onImproveAdvertisementsChange(!improveAdvertisements)}
      >
        {label}
      </span>
    )

    return (
      <ImproveAds className="t-ImproveAds">
        <LabeledWrapper label={labelNode} errors={improveAdvertisementsErrors} twoLines>
          <div className="jq-checkbox">
            <Checkbox
              onChange={this.props.onImproveAdvertisementsChange}
              checked={improveAdvertisements}
            />
          </div>
          <ImproveAdsHint
            onClick={() => this.props.onImproveAdvertisementsChange(!improveAdvertisements)}
          >
            {t('hint')}
          </ImproveAdsHint>
        </LabeledWrapper>
      </ImproveAds>
    )
  }

  renderAdminBudgetPortions() {
    const { budgetPortions } = this.props

    return (
      this.props.visibility.budgetPortions && (
        <div style={{ display: this.props.isAdminFieldShown ? 'block' : 'none' }}>
          Budget Portions
          <div className="portion">
            {this.props.initial.budgetPortions.providers.map((provider, index) =>
              this.renderBudgetPortionSelect(provider, budgetPortions[provider], index)
            )}
          </div>
        </div>
      )
    )
  }

  renderBudgetPortionSelect(provider: string, value: string, key: number) {
    return (
      <Fragment key={key}>
        {provider}: {}
        <select
          onChange={(event) => {
            // cause of life of event
            const value = event.target.value
            const { budgetPortions } = this.props

            if (value === '') {
              this.props.onBudgetPortionsChange(_.omit(budgetPortions, provider))
            } else {
              this.props.onBudgetPortionsChange({ ...budgetPortions, [provider]: value })
            }
          }}
          value={value || ''}
        >
          <option />
          <option value="0">0</option>
          <option value="1">1</option>
        </select>{' '}
      </Fragment>
    )
  }

  renderConversions() {
    if (!this.props.isConversions) return null

    const { conversion } = this.props.initial

    return (
      <Conversion
        conversions={this.props.conversions}
        isDisabled={this.props.isEditAction}
        conversionData={conversion}
        conversionId={this.props.conversionId}
        conversionIdErrors={this.props.conversionIdErrors}
        onConversionIdChange={this.props.onConversionIdChange}
        onConversionsChange={this.props.onConversionsChange}
      />
    )
  }

  renderLeadform() {
    if (!this.props.isLeadform) return null

    return (
      <LeadformSelectContainer
        isDisabled={this.props.isEditAction}
        errors={this.props.leadformIdErrors}
        adAccountId={this.props.initial.currentAdAccountId}
        configs={this.props.initial.configs}
        socialPublicProps={this.props.initial.socialPublics}
      />
    )
  }

  renderTargeting() {
    return (
      <div>
        {this.renderIncludedGroups()}
        {this.renderIncludedVkApps()}
        <AdditionalParameters link={t('additional_params')}>
          {this.renderVkActivityGroups()}
          {this.renderRelationshipStatus()}
          {this.renderBirthday()}
          {this.renderExcludedGroups()}
          {this.renderTravellers()}
          {this.renderVkUserDevices()}
          {this.renderVkPositions()}
          {this.renderFbPositions()}
          {this.renderExcludedVkApps()}
          {this.renderRetargetingHint()}
          <TargetingSourcesContainer
            onChange={() => {
              this.props.onSetCopyDefaultValues()
              this.props.onTriggerEstimates()
            }}
          />
          {this.props.visibility.vkEventGroups && <VkEventGroupsContainer />}
        </AdditionalParameters>
      </div>
    )
  }

  renderRetargeting() {
    return (
      <div>
        {this.renderRetargetingHint()}
        <TargetingSourcesContainer
          onChange={() => {
            this.props.onSetCopyDefaultValues()
            this.props.onTriggerEstimates()
          }}
        />
        <AdditionalParameters link={t('additional_params')}>
          {this.props.visibility.vkEventGroups && <VkEventGroupsContainer />}
          {this.targetingWidgets()}
          {this.renderIncludedGroups()}
          {this.renderIncludedVkApps()}
          {this.renderVkActivityGroups()}
          {this.renderRelationshipStatus()}
          {this.renderBirthday()}
          {this.renderExcludedGroups()}
          {this.renderExcludedVkApps()}
          {this.renderTravellers()}
          {this.renderVkUserDevices()}
          {this.renderVkPositions()}
          {this.renderFbPositions()}
        </AdditionalParameters>
      </div>
    )
  }

  renderPaymentType() {
    return (
      <LabeledWrapper label={t('payment_type_label')}>
        <PaymentTypeSelect
          options={this.paymentTypeOptions()}
          value={this.props.paymentType}
          onChange={this.props.onPaymentTypeChange}
        />
      </LabeledWrapper>
    )
  }

  renderBirthday() {
    if (!this.props.visibility.vk) return null

    return (
      <LabeledWrapper
        label={this.props.initial.birthday.label}
        errors={this.props.birthdayErrors}
        twoLines
      >
        <SmallInputCell>
          <Select
            options={this.props.initial.birthday.options.map((item) => ({
              id: item[1],
              name: item[0]
            }))}
            value={this.props.birthday}
            onChange={this.props.onBirthdayChange}
          />
        </SmallInputCell>
      </LabeledWrapper>
    )
  }

  renderRelationshipStatus() {
    return (
      <LabeledWrapper
        label={this.props.initial.relationshipStatus.label}
        errors={this.props.relationshipStatusErrors}
        twoLines
      >
        <SmallInputCell>
          <Select
            options={this.props.initial.relationshipStatus.options.map((item) => ({
              id: item[1],
              name: item[0]
            }))}
            value={this.props.relationshipStatus}
            onChange={this.props.onRelationshipStatusChange}
          />
        </SmallInputCell>
      </LabeledWrapper>
    )
  }

  renderExcludedGroups() {
    if (!this.props.visibility.vk && !this.props.visibility.tm) return null

    const t = translateFunc(translations.audience_edit.targeting.excluded_groups)

    const labelBlock = (
      <HintWrapper
        triggerText={t('excluded_groups')}
        popupText={t('excluded_groups_hint')}
        alignLeft
      />
    )

    return (
      <LabeledWrapper
        className="t-ExcludedVkGroupsSelect"
        label={labelBlock}
        errors={this.props.excludedGroupsErrors}
        twoLines
      >
        <VkGroupsSelect
          values={this.props.excludedGroups}
          loadedGroups={this.props.loadedExcludedGroups}
          onLoad={this.props.onLoadedExcludedGroupsChange}
          onChange={this.props.onExcludedGroupsChange}
        />
      </LabeledWrapper>
    )
  }

  renderIncludedGroups() {
    if (!this.props.visibility.vk && !this.props.visibility.tm) return null

    return (
      <IncludedVkGroupsSelectContainer
        selectClassName="t-IncludedVkGroupsSelect"
        buttonClassName="t-IncludedVkGroupsAddButton"
        blockInputName="audience_vk_group_name"
        allowAdding={this.props.isNewAction || this.props.isCopyAction}
        onChange={this.props.onIncludedGroupsChange}
        onLoad={this.props.onLoadedIncludedGroupsChange}
      />
    )
  }

  renderIncludedVkApps() {
    if (!this.props.visibility.vk && !this.props.visibility.tm) return null

    return (
      <IncludedVkAppsTextAreaContainer
        allowAdding={this.props.isNewAction || this.props.isCopyAction}
        onChange={this.props.onIncludedVkAppsGroupsChange}
      />
    )
  }

  renderExcludedVkApps() {
    if (!this.props.visibility.vk && !this.props.visibility.tm) return null

    return (
      <ExcludedVkAppsTextArea
        errors={this.props.excludedVkAppsErrors}
        value={this.props.excludedVkApps}
        onChange={this.props.onExcludedVkAppsChange}
      />
    )
  }

  renderVkActivityGroups() {
    if (!this.props.visibility.vk) return null

    return (
      <VkActivityGroupsSelectContainer
        selectClassName="t-VkActivityGroupsSelect"
        buttonClassName="t-VkActivityGroupsAddButton"
        blockInputName="audience_vk_activity_group_name"
        allowAdding={this.props.isNewAction || this.props.isCopyAction}
        onChange={this.props.onVkActivityGroupsChange}
        onLoad={this.props.onLoadedVkActivityGroupsChange}
      />
    )
  }

  renderRetargetingHint() {
    const t = translateFunc(translations.audience_edit.retargeting)
    return (
      <RetargetingHint className="retargeting-parameters">
        <div className="label">
          <RetargetingHintElementWrapper>
            <RetargetingHintTitle>{t('add_retargeting_rules')}</RetargetingHintTitle>
            <RetargetingHintQuestionMark>
              <WhatIsItHint content={t('sources_hint')} />
            </RetargetingHintQuestionMark>
          </RetargetingHintElementWrapper>
        </div>
      </RetargetingHint>
    )
  }

  renderGmap() {
    const { scriptSource, blogUrl } = this.props.initial.gmap
    const t = translateFunc(translations.audience_edit.micro_location.gmap)

    return (
      <Fragment>
        <MicrolocationsMapHelp>{t('help1')}</MicrolocationsMapHelp>
        <MicrolocationsMapHelp>
          {t('help2')}{' '}
          <a href={blogUrl} target="_blank">
            {t('help_link')}
          </a>
        </MicrolocationsMapHelp>

        <div className="micro-locations">
          <ScriptDependencyRender
            source={scriptSource}
            render={() => (
              <MicrolocationsMap
                defaultValue={this.props.localGeo}
                onChange={this.props.onLocalGeoChange}
              />
            )}
          />
          <ErrorText errors={this.props.localGeoErrors} />
        </div>
        <div>
          <LocalGeoType
            vkLocalGeoType={this.props.vkLocalGeoType}
            tmLocalGeoVisitType={this.props.tmLocalGeoVisitType}
            tmLocalGeoLocType={this.props.tmLocalGeoLocType}
            fbLocalGeoType={this.props.fbLocalGeoType}
            providers={this.props.providers}
            onChange={this.props.onLocalGeoDataChange}
          />
        </div>
      </Fragment>
    )
  }

  renderProductRetargeting() {
    if (!this.props.isProductRetargeting) return null

    return (
      <Fragment>
        <PricelistSelectContainer
          initialOptions={this.props.initial.pricelist.options}
          initialName={this.props.initial.pricelist.name}
          isDisabled={this.props.isEditAction}
          onChange={(pricelistId) => {
            this.props.onSetCopyDefaultValues()
            this.props.onPricelistIdChange(pricelistId)
          }}
        />
        {this.renderAboutCompany()}
      </Fragment>
    )
  }

  renderAboutCompany() {
    if (!this.props.visibility.tm) return null

    const { label } = this.props.initial.aboutCompany
    const t = translateFunc(translations.audience_edit.about_company)

    const labelBlock = (
      <Fragment>
        <p>{label}</p>
        <HintWrapper dashed triggerText={t('more')} popupText={t('hint')} isInteractive alignLeft />
      </Fragment>
    )

    return (
      <AboutCompanyContainer
        htmlId="audience_about_company"
        label={labelBlock}
        maxRows={5}
        maxLength={this.props.initial.configs.aboutCompanyMaxLength}
      />
    )
  }

  renderCommonAttributes(importantSpecificAttributesRenderer?: () => ReactNode) {
    return (
      <Fragment>
        {this.props.hasFormErrors && <div className="form_has_errors">{t('form_has_errors')}</div>}
        {this.renderPlacementType()}
        <SegmentationContainer hide={!this.props.visibility.segmentation} />
        {importantSpecificAttributesRenderer && importantSpecificAttributesRenderer()}
        <h2>{t('providers_label')}</h2>
        <ProvidersSetContainer
          hide={!this.props.visibility.providers}
          onChange={this.props.onTriggerEstimates}
        />
        {this.renderPlatformSelect()}
        {this.renderPlacements()}
        {this.renderSocialPublics()}
      </Fragment>
    )
  }

  renderAudienceTypeWidgets() {
    switch (this.props.audienceType) {
      case Audience.types.targeting:
      case Audience.types.conversions:
      case Audience.types.posts_promotion:
      case Audience.types.leadform:
        return this.targetingWidgets()
      case Audience.types.retargeting:
      case Audience.types.product_retargeting:
        return null
      default:
        return `Unknown audience type ${this.props.audienceType}`
    }
  }

  renderLegalInformation() {
    const { visibility, audienceType } = this.props
    const showVkDisclaimers =
      visibility.vk &&
      (audienceType === Audience.types.targeting || audienceType === Audience.types.retargeting)
    return (
      <AdditionalParameters link={t('legal_information')}>
        {this.renderAgeLimit()}
        {/* Targeting has its own field with its own validations, see renderProductRetargeting() */}
        {!this.props.isProductRetargeting && this.renderAboutCompany()}
        {showVkDisclaimers && <VkDisclaimersContainer />}
      </AdditionalParameters>
    )
  }

  renderTravellers() {
    const { visibility, onlyTravellers } = this.props

    const labelNode = (
      <span onClick={() => this.props.onOnlyTravellersChange(!onlyTravellers)}>
        {t('only_travellers')}
      </span>
    )

    return (
      visibility.vk && (
        <LabeledWrapper label={labelNode} twoLines>
          <div className="jq-checkbox">
            <Checkbox
              className="t-OnlyTravellersCheckbox"
              onChange={this.props.onOnlyTravellersChange}
              checked={onlyTravellers}
            />
          </div>
        </LabeledWrapper>
      )
    )
  }

  renderVkUserDevices() {
    const { userDevices } = this.props.initial
    const { options } = userDevices
    const { visibility, vkUserDevices } = this.props

    const t = translateFunc(translations.audience_edit.targeting.vk_user_devices)

    return (
      visibility.vk && (
        <LabeledWrapper label={t('label')} twoLines>
          <VkUserDevicesSelect className="t-VkUserDevicesSelect">
            <MultiSelect
              placeholder={t('placeholder')}
              value={vkUserDevices}
              options={options}
              onChange={this.props.onVkUserDevicesChange}
            />
          </VkUserDevicesSelect>
        </LabeledWrapper>
      )
    )
  }

  renderVkPositions() {
    const { visibility, vkPositions } = this.props
    return (
      visibility.vk && (
        <ProviderPositionsSelect
          className="t-VkPositionsSelect"
          provider="vk"
          values={vkPositions}
          onChange={this.props.onVkPositionsChange}
        />
      )
    )
  }

  renderFbPositions() {
    const { visibility, fbPositions } = this.props
    return (
      visibility.fb && (
        <ProviderPositionsSelect
          className="t-FbPositionsSelect"
          provider="facebook"
          values={fbPositions}
          onChange={this.props.onFbPositionsChange}
        />
      )
    )
  }

  targetingWidgets() {
    const { interests, demographics } = this.props.initial
    const { geographyType, demographicErrors } = this.props
    return (
      <Fragment>
        {this.props.showGeography && (
          <Geography value={geographyType} onChange={this.props.onGeographyTypeChange} />
        )}
        {geographyType === 'cities' ? this.locationsSelectWidget() : this.renderGmap()}
        {this.genderSelectWidget()}
        {this.ageRangeWidget()}
        <Interests
          selectedIds={this.props.interestIds}
          fbAdInterests={this.props.fbAdInterests}
          providerInterests={this.props.providerInterests}
          fbAdInterestsPath={interests.fbAdInterestsPath}
          errors={this.props.interestsErrors}
          vkVisible={this.props.visibility.vk}
          tmVisible={this.props.visibility.tm}
          fbVisible={this.props.visibility.fb}
          glVisible={this.props.visibility.gl}
          onSelectedIdsChange={this.props.onInterestIdsChange}
          onFbAdInterestsChange={this.props.onFbAdInterestsChange}
        />
        {(this.props.visibility.fb || this.props.visibility.tm) && (
          <Demographics
            selectedIds={this.props.demographicIds}
            providerDemographics={demographics.providerDemographics}
            errors={demographicErrors}
            fbVisible={this.props.visibility.fb}
            tmVisible={this.props.visibility.tm}
            onSelectedIdsChange={this.props.onDemographicIdsChange}
          />
        )}
        {(this.props.visibility.tm || this.props.visibility.gl) && this.renderKeywords()}
      </Fragment>
    )
  }

  renderKeywords() {
    return (
      <KeywordsTextAreaContainer
        allowAdding={this.props.isNewAction || this.props.isCopyAction}
        onChange={this.props.onKeywordsGroupsChange}
      />
    )
  }

  componentDidMount() {
    initializeStyler()
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.initialized && this.props.initialized) {
      this.props.onLoadEstimates()
    }
  }

  renderPlacementType() {
    return (
      this.props.visibility.placementType && (
        <div>
          <h2>{t('placement_type_label')}</h2>
          <div>
            <PlacementTypeSelectContainer onChange={this.props.onTriggerEstimates} />
          </div>
        </div>
      )
    )
  }

  renderPlacements() {
    const { vkPlacementOptions, vkPlacement, vkPlacementErrors } = this.props

    return (
      <div>
        <PlacementsContainer hide={!this.props.visibility.fbPlacements} />
        {this.props.visibility.vkPlacement && (
          <LabeledWrapper label={t('vk_placement_label')} twoLines>
            <NativeSelect
              options={vkPlacementOptions}
              value={vkPlacement}
              width={330}
              htmlProperties={{ disabled: !this.props.isNewAction }}
              onChange={this.props.onVkPlacementChange}
            />
            <ErrorText errors={vkPlacementErrors} marginLeft />
          </LabeledWrapper>
        )}
      </div>
    )
  }

  renderSocialPublics() {
    const { vkPublic: isVkPublicVisible, fbPublic: isFbPublicVisible } = this.props.visibility
    if (!isVkPublicVisible && !isFbPublicVisible) return null

    const { socialPublics, isOrtAccounts } = this.props.initial

    return (
      <SocialPublics
        currentAdAccountId={this.props.currentAdAccountId}
        vkPublic={this.props.vkPublic}
        fbPublic={this.props.fbPublic}
        onVkPublicChange={this.props.onVkPublicChange}
        onFbPublicChange={this.props.onFbPublicChange}
        vkPublicErrors={this.props.vkPublicErrors}
        fbPublicErrors={this.props.fbPublicErrors}
        isVkPublicVisible={isVkPublicVisible}
        isFbPublicVisible={isFbPublicVisible}
        isOrtAccounts={isOrtAccounts}
        isVkEditDisabled={
          this.props.isEditAction && !_.isEmpty(this.props.initial.audienceData.vkPublic)
        }
        isFbEditDisabled={
          this.props.isEditAction && !_.isEmpty(this.props.initial.audienceData.fbPublic)
        }
        socialPublicProps={socialPublics}
      />
    )
  }

  renderAdsList() {
    return (
      <AdsListContainer
        action={this.props.action}
        statistics={this.props.adStatistics}
        audienceData={this.audienceData()}
        errors={this.props.advertisementsErrors}
        disabled={this.props.isLoading}
        configs={this.props.initial.configs}
      />
    )
  }

  audienceData(): AudienceData {
    return {
      ...this.props.initial.audienceData,
      providers: this.props.providers,
      audienceType: this.props.audienceType,
      placementType: this.props.placementTypes[0],
      fbPlacements: this.props.fbPlacements,
      vkPlacement: this.props.vkPlacement
    }
  }

  priceRecommendationsWidget() {
    const { priceRecommendations } = this.props.initial
    const providers = this.providersWithEstimates(this.props.providers)

    if (_.isEmpty(providers)) return null
    if (!this.props.showPriceRecommendationWidget) return null

    return (
      <PriceRecommendations
        autoBids={priceRecommendations.autoBids}
        paymentType={this.props.paymentType}
        audienceType={this.props.audienceType}
        isLoading={this.props.priceRecommendationsIsLoading}
        priceRecommendations={this.props.priceRecommendations}
        providers={providers}
      />
    )
  }

  locationsSelectWidget() {
    return (
      <div>
        <IncludedLocationsSelectContainer
          allowAdding={this.props.isNewAction || this.props.isCopyAction}
          onChange={this.props.onTriggerEstimates}
        />
        <ExcludedLocationsSelectContainer onChange={this.props.onTriggerEstimates} />
      </div>
    )
  }

  isCpm() {
    return this.props.paymentType === Audience.payment_types.cpm
  }

  genderSelectWidget() {
    return <GenderSelectContainer onChange={this.props.onTriggerEstimates} />
  }

  ageRangeWidget() {
    return <AgeRangeContainer onChange={this.props.onTriggerEstimates} />
  }

  renderPlatformSelect() {
    return (
      <LabeledWrapper label={this.props.initial.platform.label}>
        <PlatformSelect
          options={this.props.initial.platform.options}
          value={this.props.platform}
          onChange={this.props.onPlatformChange}
        />
      </LabeledWrapper>
    )
  }

  providersWithEstimates(providers: Array<string>): Array<string> {
    return _.without(providers, 'google')
  }

  paymentTypeOptions() {
    const { vkPlacement } = this.props
    const { paymentType } = this.props.initial
    return _.includes(
      [
        Audience.vk_placements.promopost_without_button,
        Audience.vk_placements.promopost_with_big_image
      ],
      vkPlacement
    )
      ? [
          [t(Audience.payment_types.cpc), Audience.payment_types.cpc],
          [t(Audience.payment_types.cpm), Audience.payment_types.cpm]
        ]
      : paymentType.options
  }
}
